import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Box,
} from "@mui/material";
import ManagerApi from "../../api/ManagerApi";

interface ManagerForgotPasswordDialogProps {
  open: boolean;
  onClose: () => void;
}

const ManagerForgotPasswordDialog: React.FC<ManagerForgotPasswordDialogProps> = ({ open, onClose }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const handleSubmit = async () => {
    if (!email) {
      setError("Please enter your email address");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const message = await ManagerApi.requestPasswordReset(email.trim());
      setSuccessMessage(message);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to send password reset email. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail("");
    setError("");
    setSuccessMessage("");
    setLoading(false);
    onClose();
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !loading && email && !successMessage) {
      handleSubmit();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {successMessage ? "Reset Link Sent" : "Forgot Manager Password"}
      </DialogTitle>

      <DialogContent>
        {successMessage ? (
          <Alert severity="success">
            <Typography variant="body1">{successMessage}</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Check your email (including spam) and follow the link to set a new password.
            </Typography>
          </Alert>
        ) : (
          <Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Enter the email address on your manager account and we will send a reset link.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              autoFocus
              label="Manager Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={handleKeyPress}
              variant="outlined"
              fullWidth
              disabled={loading}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          {successMessage ? "Close" : "Cancel"}
        </Button>
        {!successMessage && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={loading || !email}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {loading ? "Sending..." : "Send Reset Link"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ManagerForgotPasswordDialog;
